import "@total-typescript/ts-reset"

import "@fontsource-variable/roboto-mono"
import "@fontsource/ibm-plex-sans/300.css"
import "@fontsource/ibm-plex-sans/400.css"
import "@fontsource/ibm-plex-sans/500.css"
import "@fontsource/ibm-plex-sans/600.css"
import "@fontsource/ibm-plex-sans/700.css"

import "./tailwind.css"

import * as Sentry from "@sentry/react"
import {
  PlaceholderDataFunction,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import { SidebarProvider } from "design-system/organisms/Sidebar"
import { ThemeProvider } from "design-system/theme"
import { PostProcessorModule } from "i18next"
import { setUpMixpanel } from "lib/mixpanel"
import { DeveloperModeProvider } from "modules/shared/components/DeveloperMode/DeveloperModeProvider"
import { resetDeveloperMode } from "modules/shared/lib/developerMode"
import React from "react"
import ReactDOM from "react-dom/client"
import { Errors } from "schema"
import { toast, Toaster } from "sonner"
import { setUpAxios, TOKEN_KEY } from "./lib/axios"
import config from "./lib/config"
import { getErrorTranslations, setupI18n } from "./lib/i18n"
import { RouterWrapper } from "./Router"

if (process.env.__APP_ENV__ === "production") {
  Sentry.init({
    dsn: config.sentryDsn,
    integrations: [],
    tracesSampleRate: 0,
    tracePropagationTargets: [config.apiEndpoint],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    environment: process.env.ENV_NAME,
    release: config.releaseVersion ?? "__unknown__",
  })
}

function onQueryError(error: Error) {
  const isAxiosError = error instanceof AxiosError

  if (!isAxiosError) {
    Sentry.captureException(error)
    return
  }

  if (error.response?.status === 401) {
    resetDeveloperMode()
    localStorage.removeItem(TOKEN_KEY)

    if (window.location.pathname.includes("/login")) return

    const redirectTo = window.location.pathname + window.location.search
    window.location.href = `/login?redirect=${encodeURIComponent(redirectTo)}`

    return
  }

  const errorTranslations = getErrorTranslations()
  const errorText: Errors = error.response?.data?.error?.key || Errors.GENERIC_ERROR

  toast.error(errorTranslations[errorText], { duration: 3000 })
}

const MAX_QUERY_RETRIES = 3

// Retrying a rate limit spends the budget that is already exhausted, keeping the client above the
// limit for as long as the tab stays open. An unauthenticated request never turns into a
// successful one either, and its retries delay the redirect to the login page.
function shouldRetryQuery(failureCount: number, error: Error) {
  const status = error instanceof AxiosError ? error.response?.status : undefined
  const shouldNotRetry = status === 429 || status === 401

  return !shouldNotRetry && failureCount < MAX_QUERY_RETRIES
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: onQueryError,
  }),
  defaultOptions: {
    queries: {
      placeholderData: (prev: PlaceholderDataFunction) => prev,
      retry: shouldRetryQuery,
    },
    mutations: {
      onError: onQueryError,
    },
  },
})

declare global {
  interface Window {
    queryClient: QueryClient
  }
}

window.queryClient = queryClient

const customPlugin: PostProcessorModule = {
  type: "postProcessor",
  name: "customProcessor",
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: Unreachable code error
  process: (value: string, key: string) => {
    return (
      <span key={"demo"} className="hover:bg-interactive hover:text-on-color" title={key}>
        {value}
      </span>
    )
  },
}

setupI18n(customPlugin)
setUpAxios()
setUpMixpanel()

ReactDOM.createRoot(document.getElementById("app")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DeveloperModeProvider>
          <SidebarProvider>
            <RouterWrapper />
          </SidebarProvider>
        </DeveloperModeProvider>
      </ThemeProvider>
    </QueryClientProvider>
    <Toaster
      position="bottom-center"
      duration={2000}
      toastOptions={{
        className: "flex items-center justify-center",
        // NOTE: We need to pass some styles by hand because otherwise
        // class names get overridden
        style: {
          backgroundColor: "var(--background)",
          color: "var(--foreground)",
          minWidth: "fit-content",
          maxWidth: "80vw",
          borderRadius: "var(--radius-lg)",
        },
        actionButtonStyle: {
          backgroundColor: "var(--primary)",
          color: "var(--primary-foreground)",
        },
        cancelButtonStyle: {
          backgroundColor: "var(--secondary)",
          color: "var(--secondary-foreground)",
        },
      }}
    />
  </React.StrictMode>,
)
